import { FiEdit2 } from 'react-icons/fi';
import Badge from '../../../../components/ui/Badge';
import PassFailToggle from '../PassFailToggle';

/**
 * Mobile distribution-board test card — visual inspection result.
 *
 * Public interface: board identity + controlled `result` / `onResultChange`
 * (+ optional `onEdit`, `tested`, `isSynced`). Hides status chrome (same as
 * DefaultTestCard), the toggle + “Visual inspection” label row, and the
 * saved/read-only + sync-badge state once a result has been recorded.
 *
 * Props:
 *   boardCode — primary title (e.g. "DB.1")
 *   assetType — secondary line (default "Distribution Board")
 *   make, model — joined as "MAKE / MODEL"
 *   location — top-right location text (e.g. "B00B R0001")
 *   result — `'pass' | 'fail' | null`
 *   onResultChange — (next) => void
 *   tested — true once a result is saved for this board; locks the toggle
 *   isSynced — when `tested`, whether the save is confirmed ("Saving…" if false)
 *   onEdit — optional edit handler
 *   disabled — disables controls regardless of `tested`
 *   className — extra class(es) on the card
 */
export default function MobileTestCardDistributionBoard({
  boardCode,
  assetType = 'Distribution Board',
  make,
  model,
  location,
  result = null,
  onResultChange,
  tested = false,
  isSynced = true,
  onEdit,
  disabled = false,
  className = '',
  ...props
}) {
  const state = result === 'pass' || result === 'fail' ? result : 'unset';
  const makeModel = [make, model].filter(Boolean).join(' / ');
  const cardStatus = result === 'pass' ? 'success' : result === 'fail' ? 'danger' : undefined;
  const locked = disabled || tested;

  return (
    <article
      className={[
        'mobile-test-card',
        'mobile-test-card--db',
        cardStatus ? `card--${cardStatus}` : '',
        className,
      ].filter(Boolean).join(' ')}
      data-state={state}
      {...props}
    >
      <div className="mobile-test-card__header">
        <div className="mobile-test-card__lead">
          <span className="mobile-test-card__sideline" aria-hidden="true" />
          <div className="mobile-test-card__identity">
            <div className="mobile-test-card__code">{boardCode}</div>
            {assetType ? (
              <div className="mobile-test-card__details">{assetType}</div>
            ) : null}
            {makeModel ? (
              <div className="mobile-test-card__details">{makeModel}</div>
            ) : null}
          </div>
        </div>

        <div className="mobile-test-card__meta">
          {location ? (
            <span className="mobile-test-card__location">{location}</span>
          ) : null}
          {tested ? (
            <Badge variant={!isSynced ? 'warning' : result === 'fail' ? 'danger' : 'success'}>
              {!isSynced ? 'Saving…' : result === 'fail' ? 'FAILED' : 'PASSED'}
            </Badge>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              className="mobile-test-card__edit"
              onClick={onEdit}
              disabled={disabled}
              aria-label="Edit distribution board"
              title="Edit"
            >
              <FiEdit2 size={16} aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      <div className="mobile-test-card-db__inspection">
        <PassFailToggle
          value={result}
          onChange={onResultChange}
          disabled={locked}
          ariaLabel={`Visual inspection for ${boardCode}`}
          className="mobile-test-card-db__toggle"
        />
        <span className="mobile-test-card-db__label">Visual inspection</span>
      </div>
    </article>
  );
}
